import { useSnackbar } from 'notistack'
import { useContext, useEffect, useState } from 'react'
import { FieldErrors } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { generatePath, useNavigate } from 'react-router-dom'

import { WorkspaceFormContext } from 'features/workspace/contexts/WorkspaceFormContext'
import { mapGraphQLErrors } from 'providers/tanstack-query/errors'
import { useMutation } from 'providers/tanstack-query/useMutation'
import { ROUTES } from 'routes'
import { AddSchema } from '../../schemas/add'
import { CREATE_WORKSPACE } from './api/mutation'
import { Data, Variables } from './api/types'
import { mapper } from './mapper'
import { UseCreateWorkspace } from './types'
import { useEnqueueSuccessSnackbar } from './useEnqueueSuccessSnackbar'

export const useCreateWorkspace = (): UseCreateWorkspace => {
  const navigate = useNavigate()
  const { t } = useTranslation('features/workspace')
  const { enqueueSnackbar } = useSnackbar()
  const { enqueueSuccessSnackbar } = useEnqueueSuccessSnackbar()
  const { setLoading } = useContext(WorkspaceFormContext)
  const [errors, setErrors] = useState<FieldErrors<AddSchema>>({})

  const { isPending, mutateAsync } = useMutation<{ input: Variables }, Data>({
    mutation: CREATE_WORKSPACE,
    onCompleted: async (data: Data) => {
      const {
        createWorkspace: { errors, workspace },
      } = data

      if (errors && errors.length > 0) {
        setErrors(mapGraphQLErrors(errors))
        enqueueSnackbar(t('form.error.create'), { variant: 'error' })
      } else if (workspace) {
        setErrors({})
        navigate(generatePath(ROUTES.WORKSPACE.LIST))
        enqueueSuccessSnackbar(workspace)
      }
    },
  })

  useEffect(() => {
    setLoading(isPending)
  }, [isPending])

  const create = (data: AddSchema) => {
    return mutateAsync({
      input: mapper(data),
    })
  }

  return { create, errors }
}
